import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  Badge,
  Paper,
  TextField,
  Slider,
  Grid,
  Divider,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Alert,
} from '@mui/material';
import {
  FavoriteBorder,
  Favorite,
  AccessTime,
  Psychology,
  SelfImprovement,
  Spa,
  LocalFlorist,
  EmojiObjects,
  DirectionsRun,
  Restaurant,
  Bedtime,
  WaterDrop,
  Air,
  Healing,
  FitnessCenter,
  Nature,
  Schedule,
  TrendingUp,
  Assessment,
  Grass,
  SetMeal,
  Agriculture,
  Pets,
  Science,
  OpenWith,
  Lock,
  ArrowBack,
  CheckCircle,
  Info,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription, FREE_LIMITS } from '../contexts/SubscriptionContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { talentTreeService } from '../services/talentTreeService';
import { weeklyLockInService, TalentSelection } from '../services/weeklyLockInService';

interface TalentNode {
  id: string;
  name: string;
  description: string;
  maxRank: number;
  currentRank: number;
  icon: React.ReactNode;
  row: number;
  col: number;
  requires?: string[]; // IDs of prerequisite talents
  unlocked: boolean;
  timeRequired: number; // Hours required per rank per week
}

interface TalentTree {
  id: string;
  name: string;
  description: string;
  color: string;
  icon: React.ReactNode;
  talents: TalentNode[];
}

// Styled components for the gaming aesthetic
const TreeBackground = styled(Box)<{ $isDark: boolean }>(({ theme, $isDark }) => ({
  background: $isDark 
    ? 'radial-gradient(ellipse at center, #1a0033 0%, #000000 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)',
  borderRadius: '16px',
  border: $isDark ? '2px solid #4a148c' : '2px solid #d4af37',
  padding: theme.spacing(2),
  position: 'relative',
  overflow: 'hidden',
  minHeight: '400px',
}));

const TalentNodeButton = styled(IconButton)<{ 
  $unlocked: boolean; 
  $maxed: boolean;
  $available: boolean;
  $treeColor: string;
  $hasRanks: boolean;
  $isDark: boolean;
}>(({ $unlocked, $maxed, $available, $treeColor, $hasRanks, $isDark }) => ({
  width: 60,
  height: 60,
  border: `3px solid ${
    $hasRanks ? '#ffd700' : 
    $unlocked ? $treeColor : 
    $available ? ($isDark ? '#666' : '#9ca3af') : ($isDark ? '#333' : '#d1d5db')
  }`,
  backgroundColor: $isDark 
    ? ($unlocked ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.6)')
    : ($unlocked ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.6)'),
  position: 'relative',
  margin: '8px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: $isDark 
      ? ($unlocked ? 'rgba(0, 0, 0, 0.9)' : 'rgba(0, 0, 0, 0.7)')
      : ($unlocked ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0.8)'),
    transform: ($unlocked || $available) ? 'scale(1.1)' : 'scale(1)',
    boxShadow: ($unlocked || $available) ? `0 0 20px ${$hasRanks ? '#ffd700' : $treeColor}` : 'none',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '28px',
    color: $hasRanks ? '#ffd700' : $unlocked ? $treeColor : ($isDark ? '#666' : '#9ca3af'),
    filter: $unlocked ? 'drop-shadow(0 0 4px currentColor)' : 'none',
  },
}));

const TimeDisplay = styled(Box)<{ $isDark: boolean }>(({ theme, $isDark }) => ({
  position: 'sticky',
  top: theme.spacing(2),
  backgroundColor: $isDark ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
  border: $isDark ? '2px solid #4a148c' : '2px solid #d4af37',
  borderRadius: '12px',
  padding: theme.spacing(2),
  color: $isDark ? '#fff' : '#1a202c',
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  zIndex: 10,
  boxShadow: $isDark ? 'none' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
}));

const TalentTree: React.FC = () => {
  const { user } = useAuth();
  const { isPremium } = useSubscription();
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedTree, setSelectedTree] = useState(0);
  const [selectedTalent, setSelectedTalent] = useState<TalentNode | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [weeklyHours, setWeeklyHours] = useState(6); // Default 6 hours per week
  const [totalSpentTime, setTotalSpentTime] = useState(0); // Total time spent across all trees
  const [userAge, setUserAge] = useState<number | null>(null);
  const [currentIncome, setCurrentIncome] = useState<number>(75000); // Default median income
  const [agePromptOpen, setAgePromptOpen] = useState(false);

  // Lock-in mode state
  const isLockInMode = searchParams.get('mode') === 'lockin';
  const isModifyMode = searchParams.get('modify') === 'true';
  const [lockInSelections, setLockInSelections] = useState<TalentSelection[]>([]);
  const [lockInNotes, setLockInNotes] = useState('');
  const [showLockInConfirm, setShowLockInConfirm] = useState(false);
  
  // Rank selection state
  const [rankSelectionOpen, setRankSelectionOpen] = useState(false);
  const [selectedTalentForRanks, setSelectedTalentForRanks] = useState<{ talent: TalentNode; tree: TalentTree } | null>(null);
  const [selectedRanks, setSelectedRanks] = useState(1);


  // Handle incoming hours from assessment or localStorage
  useEffect(() => {
    const hoursFromUrl = searchParams.get('hours');
    const savedHours = localStorage.getItem('recommendedWeeklyHours');
    
    if (hoursFromUrl) {
      const hours = parseFloat(hoursFromUrl);
      if (hours >= 0 && hours <= 25) {
        setWeeklyHours(hours);
        talentTreeService.setWeeklyCommitment(hours);
        // Clear the URL parameter after setting
        const url = new URL(window.location.href);
        url.searchParams.delete('hours');
        window.history.replaceState(null, '', url.toString());
      }
    } else if (savedHours) {
      const hours = parseFloat(savedHours);
      if (hours >= 0 && hours <= 25) {
        setWeeklyHours(hours);
        talentTreeService.setWeeklyCommitment(hours);
      }
    } else {
      // Load from talent tree service
      const serviceHours = talentTreeService.getWeeklyCommitment();
      setWeeklyHours(serviceHours);
    }
  }, [searchParams]);

  // Load age and income from localStorage or prompt for them
  useEffect(() => {
    const savedAge = localStorage.getItem('userAge');
    const savedIncome = localStorage.getItem('currentIncome');
    
    if (savedAge) {
      const age = parseInt(savedAge);
      if (age >= 18 && age <= 100) {
        setUserAge(age);
      }
    }
    
    if (savedIncome) {
      const income = parseFloat(savedIncome);
      if (income > 0) {
        setCurrentIncome(income);
      }
    }
    
    // If no age is saved, prompt for it
    if (!savedAge) {
      setAgePromptOpen(true);
    }
  }, []);

  // Load existing lock-in data if in modify mode
  useEffect(() => {
    const loadExistingLockIn = async () => {
      if (isLockInMode && isModifyMode && user) {
        try {
          const currentLocking = await weeklyLockInService.getCurrentLocking(user.id);
          if (currentLocking) {
            const existingSelections: TalentSelection[] = currentLocking.locked_talents.map(talent => {
              // Find the talent in the trees to get base time info
              const talentNode = talentTrees.flat().find(tree => 
                tree.talents.find(t => t.id === talent.talent_id)
              )?.talents.find(t => t.id === talent.talent_id);
              
              const baseTimePerRank = talentNode?.timeRequired || 0;
              const selectedRanks = baseTimePerRank > 0 ? Math.round(talent.target_minutes_per_week / baseTimePerRank) : 1;
              
              return {
                talent_id: talent.talent_id,
                talent_name: talent.talent_name,
                target_minutes_per_week: talent.target_minutes_per_week,
                tree_type: talent.tree_type,
                selected_ranks: selectedRanks,
                base_time_per_rank: baseTimePerRank
              };
            });
            setLockInSelections(existingSelections);
            setLockInNotes(currentLocking.notes || '');
          }
        } catch (error) {
          console.error('Error loading existing lock-in:', error);
        }
      }
    };

    loadExistingLockIn();
  }, [isLockInMode, isModifyMode, user]);

  // Lock-in functions
  // Check if a talent can be selected for lock-in (considering current selections + existing unlocked talents)
  const canSelectForLockIn = (talent: TalentNode, tree: TalentTree): boolean => {
    // If talent is already unlocked from current progress, it can be selected
    if (talent.unlocked) return true;
    
    // If talent has no requirements, it can be selected
    if (!talent.requires || talent.requires.length === 0) return true;
    
    // Check if all requirements are met through either:
    // 1. Current talent progress (currentRank > 0)
    // 2. Current lock-in selections
    const findTalentInAllTrees = (talentId: string) => {
      for (const searchTree of talentTrees) {
        const foundTalent = searchTree.talents.find(t => t.id === talentId);
        if (foundTalent) return foundTalent;
      }
      return null;
    };

    // Special case for talents with either/or requirements
    if (talent.id === 'longevity-fiber-intake' || talent.id === 'longevity-protein' || talent.id === 'longevity-whole-food-eating') {
      return talent.requires.some(reqId => {
        const reqTalent = findTalentInAllTrees(reqId);
        if (!reqTalent) return false;
        
        // Check if requirement is met through current progress or lock-in selection
        return reqTalent.currentRank > 0 || lockInSelections.some(s => s.talent_id === reqId);
      });
    }

    // Default case: all requirements must be met
    return talent.requires.every(reqId => {
      const reqTalent = findTalentInAllTrees(reqId);
      if (!reqTalent) return false;
      
      // Special requirement: Zone Five Cardio needs 3 ranks of Zone Two Cardio
      if (talent.id === 'longevity-zone-five' && reqId === 'longevity-cellular') {
        const lockInSelection = lockInSelections.find(s => s.talent_id === reqId);
        const totalRanks = reqTalent.currentRank + (lockInSelection?.selected_ranks || 0);
        return totalRanks >= 3;
      }
      
      // Default requirement: just needs 1 rank from either current progress or lock-in
      return reqTalent.currentRank > 0 || lockInSelections.some(s => s.talent_id === reqId);
    });
  };

  const handleLockInTalentClick = (talent: TalentNode, tree: TalentTree) => {
    const isSelected = lockInSelections.some(s => s.talent_id === talent.id);
    
    if (isSelected) {
      // Already selected - do nothing on left click
      // User needs to right-click to remove
      return;
    } 
    
    // Check if talent can be selected (prerequisites met)
    if (!canSelectForLockIn(talent, tree)) {
      toast.error(`Cannot select ${talent.name}. Prerequisites not met.`);
      return;
    }
    
    // Check premium user talent limit
    if (!isPremium && lockInSelections.length >= 3) {
      toast.error('Free users can only select up to 3 talents. Upgrade to premium to unlock unlimited talent selection.');
      return;
    }
    
    // Check if talent has multiple ranks
    if (talent.maxRank > 1) {
      // Open rank selection dialog
      setSelectedTalentForRanks({ talent, tree });
      setSelectedRanks(1);
      setRankSelectionOpen(true);
    } else {
      // Add single-rank talent directly
      const newSelection: TalentSelection = {
        talent_id: talent.id,
        talent_name: talent.name,
        target_minutes_per_week: talent.timeRequired,
        tree_type: tree.id as 'longevity' | 'healthspan' | 'wellness',
        selected_ranks: 1,
        base_time_per_rank: talent.timeRequired
      };
      setLockInSelections(prev => [...prev, newSelection]);
    }
  };

  const handleLockInTalentRightClick = (talent: TalentNode, tree: TalentTree) => {
    const isSelected = lockInSelections.some(s => s.talent_id === talent.id);
    
    if (isSelected) {
      // Check if removing this talent would break dependencies for other selected talents
      const dependentSelections = lockInSelections.filter(selection => {
        if (selection.talent_id === talent.id) return false; // Skip the talent we're removing
        
        // Find the talent for this selection
        const selectedTalent = talentTrees
          .flatMap(t => t.talents)
          .find(t => t.id === selection.talent_id);
          
        if (!selectedTalent || !selectedTalent.requires) return false;
        
        // Check if this talent is required by the dependent talent
        if (!selectedTalent.requires.includes(talent.id)) return false;
        
        // Special check for Zone Five Cardio which needs 3 ranks of Zone Two Cardio
        if (selection.talent_id === 'longevity-zone-five' && talent.id === 'longevity-cellular') {
          const currentCellularTalent = talentTrees
            .flatMap(t => t.talents)
            .find(t => t.id === 'longevity-cellular');
          const cellularSelection = lockInSelections.find(s => s.talent_id === 'longevity-cellular');
          
          if (currentCellularTalent) {
            // Check if removing the lock-in selection still leaves enough ranks
            const remainingRanks = currentCellularTalent.currentRank;
            if (remainingRanks < 3) {
              return true; // This would break Zone Five Cardio requirement
            }
          }
          return false;
        }
        
        // Check if requirement would still be met through existing talent progress
        const requiredTalent = talentTrees
          .flatMap(t => t.talents)
          .find(t => t.id === talent.id);
        
        // If the required talent has current ranks, dependency is still met
        if (requiredTalent && requiredTalent.currentRank > 0) {
          return false;
        }
        
        return true; // Dependency would be broken
      });
      
      if (dependentSelections.length > 0) {
        const dependentNames = dependentSelections.map(s => s.talent_name).join(', ');
        toast.error(`Cannot remove ${talent.name}. It is required by: ${dependentNames}`);
        return;
      }
      
      // Remove from selection on right click
      setLockInSelections(prev => prev.filter(s => s.talent_id !== talent.id));
    }
    // If not selected, do nothing on right click
  };

  const handleRankSelection = (ranks: number) => {
    if (!selectedTalentForRanks) return;
    
    const { talent, tree } = selectedTalentForRanks;
    
    // Double-check prerequisites before adding (shouldn't be necessary but good safety check)
    if (!canSelectForLockIn(talent, tree)) {
      toast.error(`Cannot select ${talent.name}. Prerequisites not met.`);
      setRankSelectionOpen(false);
      setSelectedTalentForRanks(null);
      return;
    }
    
    const totalMinutes = talent.timeRequired * ranks;
    
    const newSelection: TalentSelection = {
      talent_id: talent.id,
      talent_name: talent.name,
      target_minutes_per_week: totalMinutes,
      tree_type: tree.id as 'longevity' | 'healthspan' | 'wellness',
      selected_ranks: ranks,
      base_time_per_rank: talent.timeRequired
    };
    
    setLockInSelections(prev => [...prev, newSelection]);
    setRankSelectionOpen(false);
    setSelectedTalentForRanks(null);
  };

  const updateLockInMinutes = (talentId: string, minutes: number) => {
    setLockInSelections(prev =>
      prev.map(selection => {
        if (selection.talent_id === talentId) {
          // If this is a multi-rank talent, calculate ranks based on minutes
          const baseTimePerRank = selection.base_time_per_rank || selection.target_minutes_per_week;
          const calculatedRanks = baseTimePerRank > 0 ? Math.round(minutes / baseTimePerRank) : 1;
          
          return {
            ...selection,
            target_minutes_per_week: minutes,
            selected_ranks: calculatedRanks
          };
        }
        return selection;
      })
    );
  };

  const updateLockInRanks = (talentId: string, ranks: number) => {
    setLockInSelections(prev =>
      prev.map(selection => {
        if (selection.talent_id === talentId) {
          const baseTimePerRank = selection.base_time_per_rank || selection.target_minutes_per_week;
          const totalMinutes = baseTimePerRank * ranks;
          
          return {
            ...selection,
            target_minutes_per_week: totalMinutes,
            selected_ranks: ranks
          };
        }
        return selection;
      })
    );
  };

  const getTotalLockInMinutes = () => {
    return lockInSelections.reduce((sum, selection) => sum + selection.target_minutes_per_week, 0);
  };

  const validateLockInSelection = () => {
    if (lockInSelections.length < 3) {
      toast.error('You must select at least 3 talents.');
      return false;
    }
    
    // Check premium user talent limit
    if (!isPremium && lockInSelections.length > 3) {
      toast.error('Free users can only select up to 3 talents. Upgrade to premium for unlimited talent selection.');
      return false;
    }
    
    if (getTotalLockInMinutes() > 1500) {
      toast.error('Total time commitment cannot exceed 1500 minutes (25 hours) per week.');
      return false;
    }
    return true;
  };

  const handleCreateLockIn = async () => {
    if (!user) return;
    
    if (!validateLockInSelection()) return;

    // Debug logging
    console.log('Creating/updating lock-in with data:', {
      userId: user.id,
      lockInSelections,
      lockInNotes,
      isModifyMode,
      totalMinutes: getTotalLockInMinutes(),
      selectionCount: lockInSelections.length
    });

    // Default to using original selections
    let selectionsToUse = lockInSelections;

    // Validate each selection has required fields with detailed logging
    const invalidSelections = lockInSelections.filter((selection, index) => {
      const issues = [];
      
      if (!selection.talent_id) issues.push('missing talent_id');
      if (!selection.talent_name) issues.push('missing talent_name');
      if (!selection.tree_type) issues.push('missing tree_type');
      if (typeof selection.target_minutes_per_week !== 'number') issues.push(`target_minutes_per_week is ${typeof selection.target_minutes_per_week}, not number`);
      if (selection.target_minutes_per_week <= 0) issues.push(`target_minutes_per_week is ${selection.target_minutes_per_week}, should be > 0`);
      
      if (issues.length > 0) {
        console.error(`Selection ${index + 1} is invalid:`, selection, 'Issues:', issues);
        return true;
      }
      return false;
    });

    // Instead of blocking, try to fix common issues
    if (invalidSelections.length > 0) {
      console.warn(`Found ${invalidSelections.length} invalid selections, attempting to fix...`);
      
      // Filter out completely invalid selections and fix fixable ones
      const fixedSelections = lockInSelections.filter(selection => {
        // Keep selections that have basic required fields
        return selection.talent_id && selection.talent_name && selection.tree_type;
      }).map(selection => ({
        ...selection,
        // Fix target_minutes_per_week if it's invalid
        target_minutes_per_week: (typeof selection.target_minutes_per_week === 'number' && selection.target_minutes_per_week > 0) 
          ? selection.target_minutes_per_week 
          : (selection.base_time_per_rank || 15) // Default to base time or 15 minutes
      }));
      
      console.log(`Fixed selections from ${lockInSelections.length} to ${fixedSelections.length}:`, fixedSelections);
      
      // Update the selections with fixed data
      setLockInSelections(fixedSelections);
      
      // If we still don't have enough valid selections, show error
      if (fixedSelections.length < 3) {
        toast.error('Not enough valid talent selections. Please refresh the page and try again.');
        return;
      }
      
             // Use the fixed selections for the API call
       selectionsToUse = fixedSelections;
    }

    // Log each selection for debugging
    selectionsToUse.forEach((selection, index) => {
      console.log(`Selection ${index + 1}:`, {
        talent_id: selection.talent_id,
        talent_name: selection.talent_name,
        target_minutes_per_week: selection.target_minutes_per_week,
        tree_type: selection.tree_type,
        selected_ranks: selection.selected_ranks,
        base_time_per_rank: selection.base_time_per_rank
      });
    });

    try {
      if (isModifyMode) {
        // Update existing lock-in
        console.log('Fetching current locking for user:', user.id);
        const currentLocking = await weeklyLockInService.getCurrentLocking(user.id);
        console.log('Current locking found:', currentLocking);
        
        if (currentLocking) {
          console.log('Updating locking with ID:', currentLocking.id);
          await weeklyLockInService.updateLocking(currentLocking.id, selectionsToUse, lockInNotes);
          toast.success('Weekly lock-in updated successfully!');
        } else {
          throw new Error('No existing lock-in found to update');
        }
      } else {
        // Create new lock-in
        console.log('🔄 [TalentTree] Creating new locking for user:', user.id);
        console.log('🔄 [TalentTree] Selections to create:', selectionsToUse);
        const newLocking = await weeklyLockInService.createLocking(user.id, selectionsToUse, lockInNotes);
        console.log('✅ [TalentTree] Lock-in service call completed, received:', newLocking);
        toast.success('Weekly lock-in created successfully!');
      }
      
      // Navigate and hard refresh to ensure fresh data loads
      console.log('✅ [TalentTree] Lock-in created successfully, preparing navigation...');
      console.log('🔄 [TalentTree] Current URL before navigation:', window.location.href);
      console.log('🔄 [TalentTree] Waiting for database transaction to fully commit...');
      
      // Wait for database transaction to fully commit, then navigate with hard refresh
      setTimeout(() => {
        console.log('🚀 [TalentTree] Executing hard navigation to /weekly-lockin');
        console.log('🚀 [TalentTree] This should trigger a complete page reload with fresh data');
        
        // Force a complete page reload by setting the href directly
        // This is equivalent to typing the URL in the address bar and pressing Enter
        window.location.href = window.location.origin + '/weekly-lockin';
      }, 2000); // Increased to 2 seconds to ensure database commit
    } catch (error) {
      console.error('Error creating/updating lock-in:', error);
      
      // Extract more detailed error information
      let errorMessage = 'Failed to save lock-in. Please try again.';
      
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        errorMessage = `Failed to save lock-in: ${error.message}`;
      } else if (typeof error === 'object' && error !== null) {
        console.error('Error details:', JSON.stringify(error, null, 2));
        
        // Handle Supabase errors specifically
        if ('code' in error && 'message' in error) {
          console.error('Supabase error code:', error.code);
          console.error('Supabase error message:', error.message);
          errorMessage = `Database error: ${error.message}`;
        } else if ('message' in error) {
          errorMessage = `Failed to save lock-in: ${error.message}`;
        }
        
        // Handle PostgreSQL constraint errors
        if ('code' in error) {
          switch (error.code) {
            case '23505':
              errorMessage = 'A lock-in already exists for this week. Please update the existing one instead.';
              break;
            case '23503':
              errorMessage = 'Invalid data reference. Please refresh the page and try again.';
              break;
            case '23514':
              errorMessage = 'Invalid data values. Please check your selections and try again.';
              break;
            default:
              if ('message' in error) {
                errorMessage = `Database error (${error.code}): ${error.message}`;
              }
          }
        }
        
        // Log all properties of the error object
        console.error('Error object properties:');
        for (const [key, value] of Object.entries(error)) {
          console.error(`  ${key}:`, value);
        }
      }
      
      toast.error(errorMessage);
    }
  };

  // Initialize talent trees with default state
  const getInitialTalentTrees = (): TalentTree[] => [
    {
      id: 'longevity',
      name: 'Longevity',
      description: 'Length of Life - Extend your years through proven practices',
      color: '#9c27b0',
      icon: <AccessTime />,
      talents: [
        {
          id: 'longevity-start',
          name: 'Sleep Hygiene',
          description: 'Establish optimal sleep practices for longevity. Quality sleep is foundational to health and lifespan extension.',
          maxRank: 1,
          currentRank: 0,
          icon: <Bedtime />,
          row: 0,
          col: 3,
          unlocked: true,
          timeRequired: 15, // 15 minutes per week
        },
        {
          id: 'longevity-cellular',
          name: 'Zone Two Cardio',
          description: 'Build aerobic base through low-intensity, steady-state cardio training at 60-70% max heart rate.',
          maxRank: 7,
          currentRank: 0,
          icon: <Favorite />,
          row: 2,
          col: 1,
          requires: ['longevity-start'],
          unlocked: false,
          timeRequired: 30, // 30 minutes per week per rank
        },
        {
          id: 'longevity-metabolic',
          name: 'Strength Training',
          description: 'Build muscle mass and bone density through progressive resistance training for longevity.',
          maxRank: 3,
          currentRank: 0,
          icon: <FitnessCenter />,
          row: 2,
          col: 5,
          requires: ['longevity-start'],
          unlocked: false,
          timeRequired: 45, // 45 minutes per week per rank
        },
        {
          id: 'longevity-advanced',
          name: 'Oral Health',
          description: 'Maintain optimal oral hygiene and dental health for systemic health and longevity.',
          maxRank: 1,
          currentRank: 0,
          icon: <Healing />,
          row: 2,
          col: 3,
          requires: ['longevity-start'],
          unlocked: false,
          timeRequired: 25, // 25 minutes per week
        },
        {
          id: 'longevity-zone-five',
          name: 'Zone Five Cardio',
          description: 'High-intensity interval training at 90-100% max heart rate. 20 minutes of HIIT plus 10 minutes for warm-up and cool-down.',
          maxRank: 1,
          currentRank: 0,
          icon: <DirectionsRun />,
          row: 4,
          col: 0,
          requires: ['longevity-cellular'],
          unlocked: false,
          timeRequired: 30, // 30 minutes per week (20 min cardio + 10 min warm up/cool down)
        },
        {
          id: 'longevity-creatine',
          name: 'Creatine Supplementation',
          description: 'Enhance muscle performance and recovery with evidence-based creatine supplementation.',
          maxRank: 1,
          currentRank: 0,
          icon: <Science />,
          row: 4,
          col: 6,
          requires: ['longevity-metabolic'],
          unlocked: false,
          timeRequired: 10, // 10 minutes per week for research and habit establishment
        },
        {
          id: 'longevity-energy-balance',
          name: 'Energy Balance',
          description: 'Master caloric intake and expenditure balance to optimize body composition, metabolic health, and sustainable weight management.',
          maxRank: 1,
          currentRank: 0,
          icon: <TrendingUp />,
          row: 4,
          col: 3,
          requires: ['longevity-advanced'],
          unlocked: false,
          timeRequired: 45, // 45 minutes per week
        },
        {
          id: 'longevity-fiber-intake',
          name: 'Fiber Intake',
          description: 'Optimize daily fiber consumption targeting 25-35 grams per day from diverse whole food sources for gut health and longevity.',
          maxRank: 1,
          currentRank: 0,
          icon: <Grass />,
          row: 6,
          col: 1,
          requires: ['longevity-zone-five', 'longevity-energy-balance'],
          unlocked: false,
          timeRequired: 30, // 30 minutes per week
        },
        {
          id: 'longevity-protein',
          name: 'Protein Optimization',
          description: 'Optimize protein intake for muscle preservation, recovery, and longevity. Track and plan high-quality protein sources.',
          maxRank: 2,
          currentRank: 0,
          icon: <SetMeal />,
          row: 6,
          col: 5,
          requires: ['longevity-energy-balance', 'longevity-creatine'],
          unlocked: false,
          timeRequired: 30, // 30 minutes per week per rank
        },
        {
          id: 'longevity-time-restricted-eating',
          name: 'Time Restricted Eating',
          description: 'Implement intermittent fasting protocols to enhance metabolic health, cellular autophagy, and longevity.',
          maxRank: 1,
          currentRank: 0,
          icon: <Schedule />,
          row: 6,
          col: 3,
          requires: ['longevity-energy-balance'],
          unlocked: false,
          timeRequired: 25, // 25 minutes per week
        },

        {
          id: 'longevity-whole-food-eating',
          name: 'Whole Food, Nutrient-Dense Eating',
          description: 'Focus on unprocessed, nutrient-dense whole foods to maximize micronutrient intake and support optimal health.',
          maxRank: 2,
          currentRank: 0,
          icon: <Nature />,
          row: 7,
          col: 3,
          requires: ['longevity-fiber-intake', 'longevity-protein', 'longevity-time-restricted-eating'],
          unlocked: false,
          timeRequired: 15, // 15 minutes per week per rank
        },
      ],
    },
    {
      id: 'healthspan',
      name: 'Healthspan',
      description: 'Quality of Life - Maximize vitality and function',
      color: '#00bcd4',
      icon: <FitnessCenter />,
      talents: [
        {
          id: 'healthspan-cardio',
          name: 'Stability Mastery',
          description: 'Develop core strength, balance, and proprioception. Single-leg exercises, unstable surface training, and coordination drills.',
          maxRank: 3,
          currentRank: 0,
          icon: <SelfImprovement />,
          row: 5,
          col: 3,
          unlocked: true,
          timeRequired: 60, // 60 minutes per week per rank
        },
        {
          id: 'longevity-cellular', // Shared ID with longevity tree
          name: 'Zone Two Cardio',
          description: 'Build aerobic base through low-intensity, steady-state cardio training at 60-70% max heart rate.',
          maxRank: 7,
          currentRank: 0,
          icon: <Favorite />,
          row: 3,
          col: 0,
          requires: ['healthspan-cardio'],
          unlocked: false,
          timeRequired: 30, // 30 minutes per week per rank
        },
        {
          id: 'longevity-metabolic', // Shared ID with longevity tree
          name: 'Strength Training',
          description: 'Build muscle mass and bone density through progressive resistance training for longevity.',
          maxRank: 3,
          currentRank: 0,
          icon: <FitnessCenter />,
          row: 3,
          col: 6,
          requires: ['healthspan-cardio'],
          unlocked: false,
          timeRequired: 45, // 45 minutes per week per rank
        },
        {
          id: 'healthspan-fitness',
          name: 'Functional Mobility',
          description: 'Enhance range of motion, joint health, and movement quality. Daily mobility routines and corrective exercises.',
          maxRank: 3,
          currentRank: 0,
          icon: <OpenWith />,
          row: 4,
          col: 3,
          requires: ['healthspan-cardio'],
          unlocked: false,
          timeRequired: 10, // 10 minutes per week per rank
        },
        {
          id: 'longevity-zone-five', // Shared ID with longevity tree
          name: 'Zone Five Cardio',
          description: 'High-intensity interval training at 90-100% max heart rate. 20 minutes of HIIT plus 10 minutes for warm-up and cool-down.',
          maxRank: 1,
          currentRank: 0,
          icon: <DirectionsRun />,
          row: 2,
          col: 1,
          requires: ['longevity-cellular'],
          unlocked: false,
          timeRequired: 30, // 30 minutes per week (20 min cardio + 10 min warm up/cool down)
        },
        {
          id: 'healthspan-flexibility',
          name: 'Elastic Edge',
          description: 'Develop deep flexibility and tissue elasticity. Dynamic stretching, PNF techniques, and advanced flexibility protocols.',
          maxRank: 2,
          currentRank: 0,
          icon: <Air />,
          row: 2,
          col: 5,
          requires: ['longevity-metabolic'],
          unlocked: false,
          timeRequired: 15, // 15 minutes per week per rank
        },
        {
          id: 'healthspan-recovery',
          name: 'Recovery Mastery',
          description: 'Master recovery through temperature therapies. Sauna sessions, cold plunges, ice baths, and contrast therapy protocols.',
          maxRank: 1,
          currentRank: 0,
          icon: <Spa />,
          row: 1,
          col: 0,
          requires: ['longevity-zone-five'],
          unlocked: false,
          timeRequired: 30, // 30 minutes per week per rank
        },
        {
          id: 'healthspan-alcohol',
          name: 'Alcohol Elimination',
          description: 'Eliminate or significantly reduce alcohol consumption for optimal health, recovery, and performance. Track progress and develop healthy alternatives.',
          maxRank: 1,
          currentRank: 0,
          icon: <WaterDrop />,
          row: 1,
          col: 3,
          requires: ['healthspan-fitness'],
          unlocked: false,
          timeRequired: 0, // 0 minutes per week - elimination doesn't require time allocation
        },
        {
          id: 'healthspan-micronutrients',
          name: 'Micronutrients Sufficiency',
          description: 'Optimize essential micronutrient levels through testing and targeted supplementation. Focus only on supplementing micronutrients you are deficient in, such as magnesium, omega-3s, vitamin D3, and other critical nutrients based on your individual needs.',
          maxRank: 1,
          currentRank: 0,
          icon: <Science />,
          row: 1,
          col: 6,
          requires: ['healthspan-flexibility'],
          unlocked: false,
          timeRequired: 10, // 10 minutes per week for testing, research, and supplementation tracking
        },

      ],
    },
    {
      id: 'wellness',
      name: 'Wellness',
      description: 'Will to Live - Cultivate purpose and joy',
      color: '#4caf50',
      icon: <SelfImprovement />,
      talents: [
        {
          id: 'wellness-mindset',
          name: 'Morning Sun Seeker',
          description: 'Optimize circadian rhythms and vitamin D production through strategic morning sunlight exposure. Enhance mood, energy, and sleep quality.',
          maxRank: 5,
          currentRank: 0,
          icon: <Psychology />,
          row: 2,
          col: 3,
          unlocked: true,
          timeRequired: 10, // 10 minutes per week per rank
        },
        {
          id: 'wellness-breathwork',
          name: 'Guided Breath Work',
          description: 'Develop foundational breath awareness and regulation through guided breathing exercises. Practice daily breathing techniques for stress reduction and mental clarity.',
          maxRank: 5,
          currentRank: 0,
          icon: <Air />,
          row: 0,
          col: 1,
          requires: ['wellness-mindset'],
          unlocked: false,
          timeRequired: 5, // 5 minutes per week per rank
        },
        {
          id: 'wellness-social',
          name: 'Social Connection',
          description: 'Build and maintain meaningful relationships through family, friends, and community connections. Regular social engagement for emotional wellness and support.',
          maxRank: 3,
          currentRank: 0,
          icon: <Favorite />,
          row: 0,
          col: 5,
          requires: ['wellness-mindset'],
          unlocked: false,
          timeRequired: 30, // 30 minutes per week per rank
        },
        {
          id: 'wellness-purpose',
          name: 'Purpose & Meaning',
          description: 'Discover and align with your life purpose. Goal setting, values clarification, and personal growth for a meaningful life.',
          maxRank: 3,
          currentRank: 0,
          icon: <EmojiObjects />,
          row: 2,
          col: 6,
          requires: ['wellness-mindset'],
          unlocked: false,
          timeRequired: 10, // 10 minutes per week per rank
        },
        {
          id: 'wellness-stress',
          name: 'Emotional Regulation',
          description: 'Advanced stress management and emotional regulation. Meditation and breathwork for mental clarity and emotional stability.',
          maxRank: 2,
          currentRank: 0,
          icon: <Spa />,
          row: 4,
          col: 5,
          requires: ['wellness-mindset'],
          unlocked: false,
          timeRequired: 10, // 10 minutes per week per rank
        },
        {
          id: 'wellness-skincare',
          name: 'Skin and Hair Maintenance',
          description: 'Develop comprehensive skin and hair care routines. Daily cleansing, moisturizing, sun protection, and targeted treatments for healthy appearance and confidence.',
          maxRank: 7,
          currentRank: 0,
          icon: <LocalFlorist />,
          row: 4,
          col: 1,
          requires: ['wellness-mindset'],
          unlocked: false,
          timeRequired: 10, // 10 minutes per week per rank
        },
        {
          id: 'longevity-start', // Shared ID with longevity tree
          name: 'Sleep Hygiene',
          description: 'Establish optimal sleep practices for longevity. Quality sleep is foundational to health and lifespan extension.',
          maxRank: 1,
          currentRank: 0,
          icon: <Bedtime />,
          row: 2,
          col: 0,
          requires: ['wellness-mindset'],
          unlocked: false,
          timeRequired: 15, // 15 minutes per week
        },
      ],
    },
  ];

  // Initialize talent trees state with persistence
  const [talentTrees, setTalentTrees] = useState<TalentTree[]>(() => {
    // Load saved state on component mount
    const initialTrees = getInitialTalentTrees();
    return talentTreeService.loadTalentTreeState(initialTrees);
  });

  // Calculate total spent time across all trees and save to service
  useEffect(() => {
    const talentCounts = new Map<string, { rank: number, time: number }>();
    
    // Collect all talents and their time requirements, avoiding duplicates
    talentTrees.forEach(tree => {
      tree.talents.forEach(talent => {
        if (talent.currentRank > 0) {
          const existing = talentCounts.get(talent.id);
          if (!existing || talent.currentRank > existing.rank) {
            talentCounts.set(talent.id, {
              rank: talent.currentRank,
              time: talent.timeRequired
            });
          }
        }
      });
    });
    
    // Calculate total time from unique talents
    const spentMinutes = Array.from(talentCounts.values()).reduce((total, talent) => {
      return total + (talent.rank * talent.time);
    }, 0);
    
    const spentHours = spentMinutes / 60; // Convert minutes to hours for display
    setTotalSpentTime(spentHours);
    
    // Save talent trees to service (this will trigger dashboard updates)
    talentTreeService.saveTalentTrees(talentTrees);
  }, [talentTrees]);

  const handleTalentClick = (talent: TalentNode, tree: TalentTree) => {
    if (isLockInMode) {
      handleLockInTalentClick(talent, tree);
    } else {
    setSelectedTalent(talent);
    setDialogOpen(true);
    }
  };

  const canUpgradeTalent = (talent: TalentNode, talentTree: TalentTree): boolean => {
    // Check if talent is unlocked and not maxed
    if (!talent.unlocked || talent.currentRank >= talent.maxRank) return false;
    
    // Special check for Zone Five Cardio - needs 3 ranks of Zone Two Cardio
    if (talent.id === 'longevity-zone-five') {
      const zoneTwoTalent = talentTree.talents.find(t => t.id === 'longevity-cellular');
      if (!zoneTwoTalent || zoneTwoTalent.currentRank < 3) {
        return false;
      }
    }
    
    // Check if user has enough time available in global pool
    const timeNeededMinutes = talent.timeRequired;
    const timeNeededHours = timeNeededMinutes / 60; // Convert minutes to hours
    const timeAvailable = weeklyHours - totalSpentTime;
    
    return timeAvailable >= timeNeededHours;
  };

  const handleUpgradeTalent = () => {
    if (!selectedTalent) return;

    // Premium restriction check - allow exploration but block investment
    if (!isPremium) {
      toast.error('Upgrade to Premium to invest time in talents and track your progress! Free users can explore the health trees but cannot make time investments.');
      return;
    }

    const treeIndex = talentTrees.findIndex(tree => 
      tree.talents.some(t => t.id === selectedTalent.id)
    );
    
    if (treeIndex === -1) return;

    const currentTree = talentTrees[treeIndex];
    
    if (!canUpgradeTalent(selectedTalent, currentTree)) {
      toast.error('Not enough time available for this talent upgrade!');
      return;
    }

    setTalentTrees(prev => {
      const updated = [...prev];
      const talentIndex = updated[treeIndex].talents.findIndex(t => t.id === selectedTalent.id);
      
      // Upgrade the talent
      updated[treeIndex].talents[talentIndex].currentRank += 1;
      
      // If this is a shared node, sync it across all trees
      if (selectedTalent.id === 'longevity-metabolic' || selectedTalent.id === 'longevity-cellular' || selectedTalent.id === 'longevity-zone-five' || selectedTalent.id === 'longevity-start') {
        updated.forEach(tree => {
          const sharedTalentIndex = tree.talents.findIndex(t => t.id === selectedTalent.id);
          if (sharedTalentIndex !== -1) {
            tree.talents[sharedTalentIndex].currentRank = updated[treeIndex].talents[talentIndex].currentRank;
            tree.talents[sharedTalentIndex].unlocked = true;
          }
        });
      }
      
      // Update selectedTalent to reflect the new rank
      const updatedTalent = { ...updated[treeIndex].talents[talentIndex] };
      setSelectedTalent(updatedTalent);
      
      // Check if new talents should be unlocked across all trees
      updated.forEach((tree, idx) => {
        tree.talents.forEach(talent => {
          if (talent.requires && !talent.unlocked) {
            let requirementsMet = false;
            
            // Find requirements from the appropriate tree (check all trees for shared nodes)
            const findRequirementTalent = (reqId: string) => {
              // First check current tree
              let reqTalent = tree.talents.find(t => t.id === reqId);
              if (!reqTalent) {
                // If not found, check all trees for shared talents
                for (const otherTree of updated) {
                  reqTalent = otherTree.talents.find(t => t.id === reqId);
                  if (reqTalent) break;
                }
              }
              return reqTalent;
            };
            
            // Special case: Fiber Intake has either/or requirements
            if (talent.id === 'longevity-fiber-intake') {
              requirementsMet = talent.requires.some(reqId => {
                const reqTalent = findRequirementTalent(reqId);
                return reqTalent && reqTalent.currentRank > 0;
              });
            } 
            // Special case: Protein Optimization has either/or requirements
            else if (talent.id === 'longevity-protein') {
              requirementsMet = talent.requires.some(reqId => {
                const reqTalent = findRequirementTalent(reqId);
                return reqTalent && reqTalent.currentRank > 0;
              });
            } 
            // Special case: Whole Food Nutrient Dense Eating has either/or requirements
            else if (talent.id === 'longevity-whole-food-eating') {
              requirementsMet = talent.requires.some(reqId => {
                const reqTalent = findRequirementTalent(reqId);
                return reqTalent && reqTalent.currentRank > 0;
              });
            } else {
              // Default case: all requirements must be met
              requirementsMet = talent.requires.every(reqId => {
                const reqTalent = findRequirementTalent(reqId);
                
                // Special requirement: Zone Five Cardio needs 3 ranks of Zone Two Cardio
                if (talent.id === 'longevity-zone-five' && reqId === 'longevity-cellular') {
                  return reqTalent && reqTalent.currentRank >= 3;
                }
                
                // Default requirement: just needs 1 rank
                return reqTalent && reqTalent.currentRank > 0;
              });
            }
            
            if (requirementsMet) {
              talent.unlocked = true;
            }
          }
        });
      });
      
      return updated;
    });

    toast.success(`Upgraded ${selectedTalent.name}! Allocated ${selectedTalent.timeRequired} minutes/week.`);
    // Don't close the dialog - keep it open for further upgrades
  };

  const handleDowngradeTalent = (talent: TalentNode, talentTree: TalentTree) => {
    if (talent.currentRank <= 0) return;

    const treeIndex = talentTrees.findIndex(t => t.id === talentTree.id);
    if (treeIndex === -1) return;

    setTalentTrees(prev => {
      const updated = [...prev];
      const talentIndex = updated[treeIndex].talents.findIndex(t => t.id === talent.id);
      
      // Downgrade the talent
      updated[treeIndex].talents[talentIndex].currentRank -= 1;
      
      // If this is a shared node, sync it across all trees
      if (talent.id === 'longevity-metabolic' || talent.id === 'longevity-cellular' || talent.id === 'longevity-zone-five' || talent.id === 'longevity-start') {
        updated.forEach((tree, treeIdx) => {
          const sharedTalentIndex = tree.talents.findIndex(t => t.id === talent.id);
          if (sharedTalentIndex !== -1) {
            tree.talents[sharedTalentIndex].currentRank = updated[treeIndex].talents[talentIndex].currentRank;
            
            // Re-evaluate unlock status based on this tree's specific requirements
            if (tree.talents[sharedTalentIndex].currentRank === 0 && tree.talents[sharedTalentIndex].requires) {
              let requirementsMet = false;
              
              // Check if requirements are met in this specific tree
              const findRequirementTalent = (reqId: string) => {
                // First check current tree
                let reqTalent = tree.talents.find(t => t.id === reqId);
                if (!reqTalent) {
                  // If not found, check all trees for shared talents
                  for (const otherTree of updated) {
                    reqTalent = otherTree.talents.find(t => t.id === reqId);
                    if (reqTalent) break;
                  }
                }
                return reqTalent;
              };
              
                             // Special case for Zone Five Cardio requiring 3 ranks of Zone Two Cardio
               if (talent.id === 'longevity-zone-five') {
                 requirementsMet = (tree.talents[sharedTalentIndex].requires || []).every(reqId => {
                   const reqTalent = findRequirementTalent(reqId);
                   if (reqId === 'longevity-cellular') {
                     return reqTalent && reqTalent.currentRank >= 3;
                   }
                   return reqTalent && reqTalent.currentRank > 0;
                 });
               } else {
                 // Default case: check if all requirements are met
                 requirementsMet = (tree.talents[sharedTalentIndex].requires || []).every(reqId => {
                   const reqTalent = findRequirementTalent(reqId);
                   return reqTalent && reqTalent.currentRank > 0;
                 });
               }
              
              tree.talents[sharedTalentIndex].unlocked = requirementsMet;
            }
          }
        });
      }
      
             // If talent rank goes to 0 and it has requirements, lock it
       if (updated[treeIndex].talents[talentIndex].currentRank === 0 && talent.requires) {
         updated[treeIndex].talents[talentIndex].unlocked = false;
       }
      
      // Handle cascade effects - check all dependent talents
      const checkAndLockDependents = (targetTalentId: string) => {
        updated[treeIndex].talents.forEach(dependentTalent => {
          if (dependentTalent.requires && dependentTalent.requires.includes(targetTalentId)) {
            const updatedTargetTalent = updated[treeIndex].talents.find(t => t.id === targetTalentId);
            
            // Check if requirements are still met
            let requirementsMet = true;
            
            // Special case for Zone Five Cardio requiring 3 ranks of Zone Two Cardio
            if (dependentTalent.id === 'longevity-zone-five' && targetTalentId === 'longevity-cellular') {
              requirementsMet = updatedTargetTalent ? updatedTargetTalent.currentRank >= 3 : false;
            } else {
              requirementsMet = updatedTargetTalent ? updatedTargetTalent.currentRank > 0 : false;
            }
            
            if (!requirementsMet) {
              // Reset dependent talent completely
              const dependentIndex = updated[treeIndex].talents.findIndex(t => t.id === dependentTalent.id);
              updated[treeIndex].talents[dependentIndex].currentRank = 0;
              updated[treeIndex].talents[dependentIndex].unlocked = false;
              
              // Recursively check talents that depend on this one
              checkAndLockDependents(dependentTalent.id);
            }
          }
        });
      };
      
      // Check all talents that depend on the downgraded talent
      checkAndLockDependents(talent.id);
      
      // Re-evaluate unlock status for all talents
      updated[treeIndex].talents.forEach(t => {
        if (t.requires && t.currentRank === 0) {
          let requirementsMet = false;
          
          // Special case: Fiber Intake has either/or requirements
          if (t.id === 'longevity-fiber-intake') {
            requirementsMet = t.requires.some(reqId => {
              const reqTalent = updated[treeIndex].talents.find(talent => talent.id === reqId);
              return reqTalent && reqTalent.currentRank > 0;
            });
          } 
          // Special case: Protein Optimization has either/or requirements
          else if (t.id === 'longevity-protein') {
            requirementsMet = t.requires.some(reqId => {
              const reqTalent = updated[treeIndex].talents.find(talent => talent.id === reqId);
              return reqTalent && reqTalent.currentRank > 0;
            });
          } 
          // Special case: Whole Food Nutrient Dense Eating has either/or requirements
          else if (t.id === 'longevity-whole-food-eating') {
            requirementsMet = t.requires.some(reqId => {
              const reqTalent = updated[treeIndex].talents.find(talent => talent.id === reqId);
              return reqTalent && reqTalent.currentRank > 0;
            });
          } else {
            // Default case: all requirements must be met
            requirementsMet = t.requires.every(reqId => {
              const reqTalent = updated[treeIndex].talents.find(talent => talent.id === reqId);
              
              // Special requirement: Zone Five Cardio needs 3 ranks of Zone Two Cardio
              if (t.id === 'longevity-zone-five' && reqId === 'longevity-cellular') {
                return reqTalent && reqTalent.currentRank >= 3;
              }
              
              // Default requirement: just needs 1 rank
              return reqTalent && reqTalent.currentRank > 0;
            });
          }
          
          t.unlocked = requirementsMet;
        }
      });
      
      return updated;
    });

    const newRank = talent.currentRank - 1;
    if (newRank === 0) {
      toast.success(`Removed ${talent.name}! Freed ${talent.timeRequired} minutes/week.`);
    } else {
      toast.success(`Downgraded ${talent.name} to rank ${newRank}! Freed ${talent.timeRequired} minutes/week.`);
    }
  };

  const handleResetTree = () => {
    setTalentTrees(prev => prev.map((tree, index) => {
      if (index === selectedTree) {
        return {
          ...tree,
          talents: tree.talents.map(talent => ({
            ...talent,
            currentRank: 0,
            unlocked: talent.requires ? false : true, // Only starting talents are unlocked
          }))
        };
      }
      return tree;
    }));
    
    toast.success('Tree reset successfully!');
  };

  const renderTalentGrid = (tree: TalentTree) => {
    const maxRows = Math.max(...tree.talents.map(t => t.row)) + 1;
    const maxCols = Math.max(...tree.talents.map(t => t.col)) + 1;
    
    // Fixed grid dimensions for consistent centering
    const nodeSize = 80; // Fixed node spacing
    const nodeButtonSize = 60; // Fixed button size
    const gridWidth = maxCols * nodeSize + nodeSize; // Add extra space for edge nodes
    // Make longevity tree 20% taller for better visual spacing
    const gridHeight = tree.id === 'longevity' ? (maxRows * nodeSize * 1.2) + nodeSize : maxRows * nodeSize + nodeSize;

    // Render all talent nodes with absolute positioning
    const talentNodes = tree.talents.map(talent => {
      const isMaxed = talent.currentRank >= talent.maxRank;
      const isAvailable = canUpgradeTalent(talent, tree);
      const isSelectedForLockIn = isLockInMode && lockInSelections.some(s => s.talent_id === talent.id);
      const canSelectForLockInMode = isLockInMode ? canSelectForLockIn(talent, tree) : false;
      const isLimitedByFreeTier = isLockInMode && !isPremium && !isSelectedForLockIn && lockInSelections.length >= 3;
      const isPremiumRestricted = !isLockInMode && !isPremium && talent.unlocked; // Free users can explore but not invest
      
      // Calculate exact position for this node
      const nodeX = talent.col * nodeSize + (nodeSize - nodeButtonSize) / 2;
      const nodeY = talent.row * nodeSize + (nodeSize - nodeButtonSize) / 2;
      
      return (
        <Box 
          key={talent.id} 
          sx={{ 
            position: 'absolute',
            left: nodeX,
            top: nodeY,
            width: nodeButtonSize,
            height: nodeButtonSize,
          }}
        >
          <Tooltip 
            title={
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {talent.name} 
                  {isLockInMode ? (
                    isSelectedForLockIn ? (
                      ` (Selected: ${lockInSelections.find(s => s.talent_id === talent.id)?.selected_ranks || 1}/${talent.maxRank} ranks)`
                    ) : (
                      ` (Available: 1-${talent.maxRank} ranks)`
                    )
                  ) : (
                    ` (Rank ${talent.currentRank}/${talent.maxRank})`
                  )}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {talent.description}
                </Typography>
                <Typography variant="caption" sx={{ color: '#ccc' }}>
                  Time Required: {talent.timeRequired} minutes/week per rank
                  {talent.maxRank > 1 && (
                    ` (Max: ${talent.timeRequired * talent.maxRank} minutes for all ranks)`
                  )}
                </Typography>
                {isLockInMode && isSelectedForLockIn && (
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: '#4caf50' }}>
                    ✓ Selected: {lockInSelections.find(s => s.talent_id === talent.id)?.target_minutes_per_week} minutes/week
                    {(lockInSelections.find(s => s.talent_id === talent.id)?.selected_ranks || 1) > 1 && 
                      ` (${lockInSelections.find(s => s.talent_id === talent.id)?.selected_ranks} ranks)`
                    }
                    <br />Right-click to remove from selection
                  </Typography>
                )}
                {isLockInMode && !isSelectedForLockIn && (
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: canSelectForLockInMode && !isLimitedByFreeTier ? '#4caf50' : '#f44336' }}>
                    {isLimitedByFreeTier 
                      ? 'Free tier limit reached (3 talents max). Upgrade to premium for unlimited selection.'
                      : canSelectForLockInMode 
                        ? 'Left-click to select for lock-in' 
                        : 'Prerequisites not met'
                    }
                  </Typography>
                )}
                {!isLockInMode && isPremiumRestricted && (
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: '#ff9800' }}>
                    🔒 Premium Required: Free users can explore but cannot invest time in talents. Upgrade to Premium to track progress and unlock the full experience!
                  </Typography>
                )}
                {talent.requires && (
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: '#ff9800' }}>
                    Requires: {talent.requires.map(id => {
                      const reqTalent = tree.talents.find(t => t.id === id);
                      // Special case for Zone Five Cardio
                      if (talent.id === 'longevity-zone-five' && id === 'longevity-cellular') {
                        return `${reqTalent?.name || id} (Rank 3+)`;
                      }
                      return reqTalent?.name || id;
                    }).join(', ')}
                  </Typography>
                )}

              </Box>
            }
            arrow
            placement="top"
          >
            <span>
              <TalentNodeButton
                $unlocked={talent.unlocked}
                $maxed={isMaxed}
                $available={isAvailable}
                $treeColor={tree.color}
                $hasRanks={talent.currentRank > 0}
                $isDark={mode === 'dark'}
                onClick={() => handleTalentClick(talent, tree)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  if (isLockInMode) {
                    handleLockInTalentRightClick(talent, tree);
                  } else if (talent.currentRank > 0) {
                    handleDowngradeTalent(talent, tree);
                  }
                }}
                disabled={isLockInMode ? (!canSelectForLockInMode && !isSelectedForLockIn) || isLimitedByFreeTier : !talent.unlocked}
                sx={{ 
                  width: '100%', 
                  height: '100%',
                  ...(isSelectedForLockIn && {
                    border: '4px solid #4caf50',
                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                    boxShadow: '0 0 15px #4caf50',
                    '&:hover': {
                      backgroundColor: 'rgba(76, 175, 80, 0.3)',
                      transform: 'scale(1.1)',
                      boxShadow: '0 0 20px #4caf50',
                    }
                  }),
                  ...(isLimitedByFreeTier && {
                    opacity: 0.3,
                    filter: 'grayscale(70%)',
                    border: '3px solid #666',
                    '&:hover': {
                      opacity: 0.5,
                      transform: 'none',
                      boxShadow: 'none',
                    }
                  }),
                  ...(isPremiumRestricted && {
                    opacity: 0.85,
                    border: '3px solid #ff9800',
                    '&:hover': {
                      opacity: 1,
                      transform: 'scale(1.05)',
                      boxShadow: '0 0 10px rgba(255, 152, 0, 0.5)',
                    }
                  })
                }}
              >
                {talent.icon}
                {talent.currentRank > 0 && (
                  <Badge
                    badgeContent={talent.currentRank}
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: -5,
                      right: -5,
                      '& .MuiBadge-badge': {
                        backgroundColor: tree.color,
                        color: 'white',
                        fontSize: '0.7rem',
                        minWidth: '18px',
                        height: '18px',
                      }
                    }}
                  />
                )}
              </TalentNodeButton>
            </span>
          </Tooltip>
        </Box>
      );
    });

    // Create connection lines SVG overlay
    const connectionLines = tree.talents.map(talent => {
      if (!talent.requires) return null;
      
      return talent.requires.map(reqId => {
        // Only draw lines to talents within the same tree
        const reqTalent = tree.talents.find(t => t.id === reqId);
        if (!reqTalent) return null;
        
        // Calculate center positions of each node (more precise)
        const fromCenterX = reqTalent.col * nodeSize + nodeSize / 2;
        const fromCenterY = reqTalent.row * nodeSize + nodeSize / 2;
        const toCenterX = talent.col * nodeSize + nodeSize / 2;
        const toCenterY = talent.row * nodeSize + nodeSize / 2;
        
        // Calculate the distance between centers
        const dx = toCenterX - fromCenterX;
        const dy = toCenterY - fromCenterY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Avoid division by zero for same-position nodes
        if (distance === 0) return null;
        
        // Precise node radius calculation for perfect edge connection:
        // - nodeButtonSize = 60px (button diameter)  
        // - button radius = 30px
        // - The visual radius should be just the button radius
        const buttonRadius = nodeButtonSize / 2; // 30px
        
        // Use just the button radius - the border is visual only and doesn't affect positioning
        const nodeRadius = buttonRadius; // 30px - actual button edge
        const lineGap = 2; // Small gap to prevent overlap
        
        // Calculate unit vector (direction from source to target)
        const unitX = dx / distance;
        const unitY = dy / distance;
        
        // Calculate precise edge points where lines should start and end
        const fromX = fromCenterX + unitX * (nodeRadius + lineGap);
        const fromY = fromCenterY + unitY * (nodeRadius + lineGap);
        const toX = toCenterX - unitX * (nodeRadius + lineGap);
        const toY = toCenterY - unitY * (nodeRadius + lineGap);
        
        // Determine line color and styling based on requirement status
        let lineColor = mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.25)'; // Dark lines for light mode
        let strokeWidth = 2;
        let strokeDasharray = '5,5'; // Dashed for inactive connections
        
        // Check if requirement is met through either current progress or lock-in selection
        const reqLockInSelection = lockInSelections.find(s => s.talent_id === reqId);
        const isRequirementMet = reqTalent.currentRank > 0 || reqLockInSelection;
        
        if (isRequirementMet) {
          // Special case for Zone Five Cardio requiring 3 ranks
          if (talent.id === 'longevity-zone-five' && reqId === 'longevity-cellular') {
            const totalRanks = reqTalent.currentRank + (reqLockInSelection?.selected_ranks || 0);
            if (totalRanks >= 3) {
              lineColor = tree.color;
              strokeWidth = 3;
              strokeDasharray = 'none';
          } else {
              lineColor = 'rgba(255, 165, 0, 0.8)'; // Orange if not enough ranks
              strokeWidth = 3;
              strokeDasharray = '8,4'; // Different dash pattern
            }
          } else {
            lineColor = tree.color;
            strokeWidth = 3;
            strokeDasharray = 'none'; // Solid line for active connections
          }
        }
        
        // Use the precise edge points without additional adjustment
        // to ensure lines connect directly to the visual node edges
        const adjustedFromX = fromX;
        const adjustedFromY = fromY;
        const adjustedToX = toX;
        const adjustedToY = toY;
        
        const lineId = `line-${reqTalent.id}-${talent.id}`;
        
        return (
          <g key={`${reqTalent.id}-${talent.id}`}>
            {/* Background glow effect for active connections */}
            {isRequirementMet && (
          <line
                x1={adjustedFromX}
                y1={adjustedFromY}
                x2={adjustedToX}
                y2={adjustedToY}
            stroke={lineColor}
                strokeWidth={strokeWidth + 2}
            strokeLinecap="round"
                strokeDasharray={strokeDasharray === 'none' ? undefined : strokeDasharray}
                opacity="0.4"
              />
            )}
            {/* Main connection line */}
            <line
              x1={adjustedFromX}
              y1={adjustedFromY}
              x2={adjustedToX}
              y2={adjustedToY}
              stroke={lineColor}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={strokeDasharray === 'none' ? undefined : strokeDasharray}
              style={{ 
                transition: 'all 0.3s ease'
              }}
          />
          </g>
        );
      });
    }).flat().filter(Boolean);

    return (
      <Box 
        sx={{ 
          position: 'relative', 
          width: '100%',
          height: gridHeight,
          minHeight: 400,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: gridWidth,
            height: gridHeight,
            margin: '0 auto',
            transform: {
              xs: 'scale(0.7)', // 70% on mobile
              sm: 'scale(0.8)', // 80% on small tablets  
              md: 'scale(0.9)', // 90% on medium screens
              lg: 'scale(1)',   // 100% on large screens
            },
            transformOrigin: 'center center',
          }}
        >
          {/* SVG overlay for connection lines */}
          <svg
            width={gridWidth}
            height={gridHeight}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              pointerEvents: 'none',
              zIndex: 0,
            }}
            viewBox={`0 0 ${gridWidth} ${gridHeight}`}
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              {/* Global gradient definitions for connection lines */}
              <filter id="glow">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge> 
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
              <filter id="subtle-glow">
                <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
                <feMerge> 
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            {connectionLines}
          </svg>
          
          {/* Talent nodes with absolute positioning */}
          {talentNodes}
        </Box>
      </Box>
    );
  };

  // Calculate impact metrics based on current talent allocations
  const calculateImpactMetrics = (tree: TalentTree) => {
    let longevityYears = 0;
    let healthspanScore = 0;

    // Only count talents from the Longevity tree for years gained calculation
    // Find the longevity tree from all talent trees
    const longevityTree = talentTrees.find(t => t.id === 'longevity');
    
    if (longevityTree) {
      longevityTree.talents.forEach(talent => {
        if (talent.currentRank > 0) {
          // Base impact per rank organized by tier priority (higher tiers = more years)
          let yearImpact = 0;

          switch (talent.id) {
            // Tier 1: Foundation (Row 0) - Increased impact
            case 'longevity-start': // Sleep Hygiene
              yearImpact = 0.7; // Increased from 0.44 (+0.26)
              break;
            
            // Tier 2: Core Training (Row 2) - Significantly increased impact
            case 'longevity-cellular': // Zone Two Cardio
              yearImpact = 0.65 * talent.currentRank; // Increased from 0.49, up to 4.55 years for max ranks (7) (+1.12)
              break;
            case 'longevity-metabolic': // Strength Training - MAJOR BOOST
              yearImpact = 0.9 * talent.currentRank; // Increased from 0.54, up to 2.7 years for max ranks (3) (+1.08)
              break;
            case 'longevity-advanced': // Oral Health
              yearImpact = 0.35; // Increased from 0.22 (+0.13)
              break;
            
            // Tier 3: Advanced Training (Row 4) - High impact
            case 'longevity-zone-five': // Zone Five Cardio
              yearImpact = 1.0; // Increased from 0.76 (+0.24)
              break;
            case 'longevity-creatine': // Creatine
              yearImpact = 0.15; // Increased from 0.08 (+0.07)
              break;
            case 'longevity-energy-balance': // Energy Balance
              yearImpact = 0.55; // Increased from 0.38 (+0.17)
              break;
            
            // Tier 4: Nutrition Optimization (Row 6) - Medium impact
            case 'longevity-fiber-intake': // Fiber Intake
              yearImpact = 0.3; // Increased from 0.19 (+0.11)
              break;
            case 'longevity-protein': // Protein Optimization
              yearImpact = 0.22 * talent.currentRank; // Increased from 0.14, up to 0.44 years for max ranks (2) (+0.16)
              break;
            case 'longevity-time-restricted-eating': // Time Restricted Eating
              yearImpact = 0.25; // Increased from 0.16 (+0.09)
              break;
            
            // Tier 5: Master Nutrition (Row 7) - Highest impact
            case 'longevity-whole-food-eating': // Whole Food Eating
              yearImpact = 0.35 * talent.currentRank; // Increased from 0.22, up to 0.7 years for max ranks (2) (+0.26)
              break;
          }

          longevityYears += yearImpact;
        }
      });
    }

    // Calculate healthspan score from Healthspan and Wellness trees
    const healthspanTree = talentTrees.find(t => t.id === 'healthspan');
    const wellnessTree = talentTrees.find(t => t.id === 'wellness');

    // Healthspan tree contributions (higher tiers = more points)
    if (healthspanTree) {
      healthspanTree.talents.forEach(talent => {
        if (talent.currentRank > 0) {
          let scoreImpact = 0;

          switch (talent.id) {
            // Tier 1: Foundation (Row 0) - 1500 points
            case 'healthspan-cardio': // Stability Mastery
              scoreImpact = 500 * talent.currentRank; // 3 ranks = 1500 points
              break;
            
            // Tier 2: Core Skills (Row 1) - 2400 points total
            case 'longevity-metabolic': // Strength Training (shared)
              scoreImpact = 400 * talent.currentRank; // 3 ranks = 1200 points
              break;
            case 'longevity-cellular': // Zone Two Cardio (shared)
              scoreImpact = 200 * talent.currentRank; // 7 ranks = 1400 points (but counted once due to shared)
              break;
            
            // Tier 3: Advanced Skills (Row 2) - 5500 points total
            case 'longevity-zone-five': // Zone Five Cardio (shared)
              scoreImpact = 1000; // 1 rank = 1000 points
              break;
            case 'healthspan-fitness': // Functional Mobility
              scoreImpact = 750 * talent.currentRank; // 3 ranks = 2250 points
              break;
            case 'healthspan-flexibility': // Elastic Edge
              scoreImpact = 1125 * talent.currentRank; // 2 ranks = 2250 points
              break;
            
            // Tier 4: Master Skills (Row 3) - 6100 points total
            case 'healthspan-recovery': // Recovery Mastery
              scoreImpact = 1500; // 1 rank = 1500 points
              break;
            case 'healthspan-alcohol': // Alcohol Elimination
              scoreImpact = 1800; // 1 rank = 1800 points
              break;
            case 'healthspan-micronutrients': // Micronutrients
              scoreImpact = 2800; // 1 rank = 2800 points
              break;
          }

          healthspanScore += scoreImpact;
        }
      });
    }

    // Wellness tree contributions (higher tiers = more points)
    if (wellnessTree) {
      wellnessTree.talents.forEach(talent => {
        if (talent.currentRank > 0) {
          let scoreImpact = 0;

          switch (talent.id) {
            // Tier 1: Foundation (Row 0) - 6000 points
            case 'wellness-breathwork': // Guided Breath Work
              scoreImpact = 600 * talent.currentRank; // 5 ranks = 3000 points
              break;
            
            // Tier 2: Core Skills (Row 1) - 3000 points
            case 'wellness-mindset': // Morning Sun Seeker
              scoreImpact = 600 * talent.currentRank; // 5 ranks = 3000 points
              break;
            case 'wellness-social': // Social Connection
              scoreImpact = 1000 * talent.currentRank; // 3 ranks = 3000 points
              break;
            
            // Tier 3: Advanced Skills (Row 1-2) - 16500 points total
            case 'wellness-stress': // Emotional Regulation
              scoreImpact = 2000 * talent.currentRank; // 2 ranks = 4000 points
              break;
            case 'wellness-purpose': // Purpose & Meaning
              scoreImpact = 1333 * talent.currentRank; // 3 ranks = 4000 points
              break;
            case 'wellness-skincare': // Skin and Hair Maintenance
              scoreImpact = 1000 * talent.currentRank; // 7 ranks = 7000 points
              break;
            case 'longevity-start': // Sleep Hygiene (shared with longevity tree)
              scoreImpact = 1500; // 1 rank = 1500 points
              break;
            

          }

          healthspanScore += scoreImpact;
        }
      });
    }

    // Cap healthspan score at 30,000 max as requested
    healthspanScore = Math.min(healthspanScore, 30000);

    // Cap longevity at 12 years max as requested
    longevityYears = Math.min(longevityYears, 12); // Increased from 8

    // Calculate healthworth: future income at retirement × years gained
    let healthworth = 0;
    
    if (userAge !== null && longevityYears > 0) {
      const retirementAge = 67;
      const yearsToRetirement = Math.max(0, retirementAge - userAge);
      const annualGrowthRate = 0.02; // 2% annual growth
      
      // Calculate future income at retirement
      const futureIncomeAtRetirement = currentIncome * Math.pow(1 + annualGrowthRate, yearsToRetirement);
      
      // Healthworth = future retirement income × years gained
      healthworth = Math.round(futureIncomeAtRetirement * longevityYears);
    }

    return {
      longevityYears: Math.round(longevityYears * 10) / 10, // Round to 1 decimal
      healthspanScore: Math.round(healthspanScore),
      healthworth: healthworth
    };
  };

  const currentTree = talentTrees[selectedTree];
  const currentMetrics = calculateImpactMetrics(currentTree);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Premium Banner for Free Users */}
      {!isPremium && !isLockInMode && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3, 
            background: 'linear-gradient(45deg, #2196f3 30%, #21cbf3 90%)',
            color: 'white',
            '& .MuiAlert-icon': { color: 'white' }
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            🌟 <strong>Premium Health Trees Preview Mode</strong> - You can explore all talent trees but cannot invest time or track progress. 
            <Button 
              variant="contained" 
              size="small" 
              sx={{ 
                ml: 2, 
                backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' }
              }}
              onClick={() => navigate('/pricing')}
            >
              Upgrade to Premium
            </Button>
          </Typography>
        </Alert>
      )}

      {/* Lock-In Mode Header */}
      {isLockInMode && (
        <Paper sx={{ mb: 3, p: 3, backgroundColor: 'rgba(76, 175, 80, 0.1)', border: '2px solid #4caf50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Lock sx={{ color: '#4caf50', fontSize: 32 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                  {isModifyMode ? 'Modify Weekly Lock-In' : 'Create Weekly Lock-In'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Select 3+ talents from any tree. Maximum 25 hours (1500 minutes) per week.
                  {!isPremium && (
                    <span style={{ color: '#ff9800', fontWeight: 'bold' }}>
                      {' '}Free users limited to 3 talents. Upgrade to premium for unlimited selection.
                    </span>
                  )}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/lock-in')}
              sx={{ color: '#4caf50', borderColor: '#4caf50' }}
            >
              Back to Dashboard
            </Button>
          </Box>

          {/* Selection Summary */}
          <Box sx={{ backgroundColor: 'rgba(0, 0, 0, 0.1)', p: 2, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#4caf50' }}>
              Selection Summary ({lockInSelections.length} talents selected)
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Total Talents:</Typography>
                <Typography variant="h6" sx={{ color: lockInSelections.length >= 3 ? '#4caf50' : '#f44336' }}>
                  {lockInSelections.length}/{isPremium ? '∞' : '3'} {isPremium ? '' : 'max'}
                </Typography>
                {!isPremium && lockInSelections.length >= 3 && (
                  <Typography variant="caption" sx={{ color: '#ff9800', fontSize: '0.7rem' }}>
                    Premium for more
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Total Time:</Typography>
                <Typography variant="h6" sx={{ color: getTotalLockInMinutes() <= 1500 ? '#4caf50' : '#f44336' }}>
                  {getTotalLockInMinutes()}/1500 min
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Hours per Day:</Typography>
                <Typography variant="h6" sx={{ color: '#2196f3' }}>
                  {Math.round(getTotalLockInMinutes() / 7)} min avg
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Trees Used:</Typography>
                <Typography variant="h6" sx={{ color: '#2196f3' }}>
                  {new Set(lockInSelections.map(s => s.tree_type)).size} trees
                </Typography>
              </Grid>
            </Grid>

            {/* Selected Talents List */}
            {lockInSelections.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Selected Talents:
                </Typography>
                <Grid container spacing={1}>
                  {lockInSelections.map((selection) => {
                    const hasMultipleRanks = (selection.selected_ranks || 1) > 1 || (selection.base_time_per_rank && selection.base_time_per_rank !== selection.target_minutes_per_week);
                    const maxRanks = selection.base_time_per_rank ? Math.floor(120 / selection.base_time_per_rank) : 1;
                    
                    return (
                      <Grid item xs={12} sm={6} md={4} key={selection.talent_id}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1, backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircle sx={{ color: '#4caf50', fontSize: 16 }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {selection.talent_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {selection.tree_type}
                                {hasMultipleRanks && ` • ${selection.selected_ranks || 1} rank${(selection.selected_ranks || 1) > 1 ? 's' : ''}`}
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {hasMultipleRanks && selection.base_time_per_rank ? (
                              <>
                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>
                                  Ranks:
                                </Typography>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={selection.selected_ranks || 1}
                                  onChange={(e) => updateLockInRanks(selection.talent_id, parseInt(e.target.value) || 1)}
                                  inputProps={{ min: 1, max: maxRanks, style: { textAlign: 'center' } }}
                                  sx={{ width: 60 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  ({selection.target_minutes_per_week} min/wk)
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>
                                  Time:
                                </Typography>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={selection.target_minutes_per_week}
                                  onChange={(e) => updateLockInMinutes(selection.talent_id, parseInt(e.target.value) || 0)}
                                  inputProps={{ min: 5, max: 120, style: { textAlign: 'center' } }}
                                  sx={{ width: 70 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  min/wk
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            {/* Notes Section */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                Notes (Optional):
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                value={lockInNotes}
                onChange={(e) => setLockInNotes(e.target.value)}
                placeholder="Add any notes about your weekly goals..."
                variant="outlined"
                size="small"
              />
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/lock-in')}
                sx={{ color: '#666', borderColor: '#666' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateLockIn}
                disabled={
                  lockInSelections.length < 3 || 
                  getTotalLockInMinutes() > 1500 ||
                  (!isPremium && lockInSelections.length > 3)
                }
                sx={{ 
                  backgroundColor: '#4caf50',
                  '&:hover': { backgroundColor: '#388e3c' },
                  '&:disabled': { backgroundColor: '#666' }
                }}
              >
                {isModifyMode ? 'Update Lock-In' : 'Create Lock-In'}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Tree Selection Tabs */}
      {!isLockInMode && (
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={selectedTree} 
          onChange={(e, newValue) => setSelectedTree(newValue)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              minHeight: 80,
              fontSize: '1rem',
              fontWeight: 'bold',
            }
          }}
        >
          {talentTrees.map((tree, index) => (
            <Tab
              key={tree.id}
              label={
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    {tree.icon}
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {tree.name}
                    </Typography>
                  </Box>

                </Box>
              }
              sx={{ color: tree.color }}
            />
          ))}
        </Tabs>
      </Paper>
      )}

      {/* Lock-In Mode Tree Tabs */}
      {isLockInMode && (
        <Paper sx={{ mb: 3 }}>
          <Tabs 
            value={selectedTree} 
            onChange={(e, newValue) => setSelectedTree(newValue)}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                minHeight: 80,
                fontSize: '1rem',
                fontWeight: 'bold',
              }
            }}
          >
            {talentTrees.map((tree, index) => {
              const selectedFromTree = lockInSelections.filter(s => s.tree_type === tree.id).length;
              return (
                <Tab
                  key={tree.id}
                  label={
                    <Box sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                        {tree.icon}
                        <Typography variant="h6" sx={{ ml: 1 }}>
                          {tree.name}
                        </Typography>
                        {selectedFromTree > 0 && (
                          <Badge 
                            badgeContent={selectedFromTree} 
                            color="primary" 
                            sx={{ 
                              ml: 1,
                              '& .MuiBadge-badge': { 
                                backgroundColor: '#4caf50',
                                color: 'white' 
                              }
                            }} 
                          />
                        )}
                      </Box>
                    </Box>
                  }
                  sx={{ color: tree.color }}
                />
              );
            })}
          </Tabs>
        </Paper>
      )}

      {/* Talent Tree Display */}
      <TreeBackground $isDark={mode === 'dark'}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', lg: 'row' },
          justifyContent: { lg: 'space-between' },
          alignItems: { xs: 'center', lg: 'center' },
          gap: { xs: 2, lg: 0 },
          mb: 3 
        }}>
          {/* Tree Name and Description */}
          <Box sx={{ textAlign: { xs: 'center', lg: 'left' } }}>
            <Typography variant="h6" sx={{ color: currentTree.color, fontWeight: 'bold' }}>
              {currentTree.name}
            </Typography>
            <Typography variant="body2" sx={{ color: mode === 'dark' ? '#ccc' : '#6b7280', mb: 2 }}>
              {currentTree.description}
            </Typography>
            
            {/* Health Impact Data */}
            <Box sx={{ display: 'flex', justifyContent: { xs: 'center', lg: 'flex-start' }, alignItems: 'center', gap: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#81c784', fontWeight: 'bold', fontSize: '1.2rem' }}>
                  +{currentMetrics.longevityYears}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: mode === 'dark' ? '#ccc' : '#6b7280', fontSize: '0.75rem' }}>
                    Years Gained
                  </Typography>
                  <Tooltip 
                    title="Years gained if talent tree maintained every week until end of life"
                    placement="top"
                    arrow
                  >
                    <Info sx={{ 
                      fontSize: '0.9rem', 
                      color: mode === 'dark' ? '#ccc' : '#6b7280',
                      cursor: 'pointer',
                      opacity: 0.7,
                      '&:hover': { opacity: 1 }
                    }} />
                  </Tooltip>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#2196f3', fontWeight: 'bold', fontSize: '1.2rem' }}>
                  {currentMetrics.healthspanScore.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: mode === 'dark' ? '#ccc' : '#6b7280', fontSize: '0.75rem' }}>
                  Healthspan Score
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <TrendingUp sx={{ fontSize: '1.1rem' }} />
                  {userAge !== null && currentMetrics.healthworth > 0 ? 
                    `+$${currentMetrics.healthworth.toLocaleString()}` : 
                    '---'
                  }
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: mode === 'dark' ? '#ccc' : '#6b7280', 
                    fontSize: '0.75rem',
                    cursor: userAge === null ? 'pointer' : 'default'
                  }}
                  onClick={() => {
                    if (userAge === null) {
                      setAgePromptOpen(true);
                    }
                  }}
                >
                  Healthworth {userAge === null && '(Click to set)'}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Time Management */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            width: { xs: '100%', lg: 'auto' }
          }}>
            <Card sx={{ 
              minWidth: { xs: '100%', md: 510 }, 
              maxWidth: { xs: '100%', md: 'none' },
              backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.9)', 
              border: mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)' 
            }}>
              <CardContent sx={{ py: 2, px: { xs: 2, md: 4 } }}>
                <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium', fontSize: '1rem', display: 'block', color: mode === 'dark' ? '#fff' : '#1a202c' }}>
                      Weekly: {weeklyHours}h
                    </Typography>
                    <Slider
                      value={weeklyHours}
                      onChange={(e, value) => {
                        const hours = value as number;
                        setWeeklyHours(hours);
                        talentTreeService.setWeeklyCommitment(hours);
                      }}
                      min={0}
                      max={25}
                      step={0.5}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value}h`}
                      size="medium"
                      sx={{ 
                        height: 8, 
                        color: currentTree.color,
                        '& .MuiSlider-thumb': {
                          width: 24,
                          height: 24,
                        },
                        '& .MuiSlider-track': {
                          height: 8,
                        },
                        '& .MuiSlider-rail': {
                          height: 8,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium', fontSize: '1rem', display: 'block', color: mode === 'dark' ? '#fff' : '#1a202c' }}>
                      Used: {totalSpentTime.toFixed(1)}h ({Math.round(totalSpentTime * 60)} min)
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={weeklyHours > 0 ? (totalSpentTime / weeklyHours) * 100 : 0}
                      sx={{ 
                        height: 12, 
                        borderRadius: 6,
                        backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: totalSpentTime > weeklyHours ? '#f44336' : currentTree.color,
                        }
                      }}
                    />
                    <Typography variant="body2" color={mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'} sx={{ mt: 0.5, display: 'block', fontSize: '0.9rem' }}>
                      {weeklyHours > totalSpentTime 
                        ? `${(weeklyHours - totalSpentTime).toFixed(1)}h available`
                        : totalSpentTime > weeklyHours 
                        ? `${(totalSpentTime - weeklyHours).toFixed(1)}h over`
                        : 'Full'
                      }
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>



          {/* Reset Button and Instructions */}
          <Box sx={{ textAlign: { xs: 'center', lg: 'right' } }}>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleResetTree}
              disabled={currentTree.talents.every(t => t.currentRank === 0)}
            >
              Reset Tree
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1, color: '#4caf50' }}>
              Left click: Upgrade • Right click: Remove/Downgrade
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 3 }}>
          {renderTalentGrid(currentTree)}
        </Box>

        {totalSpentTime > weeklyHours && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            You have allocated more time than available! Consider increasing your weekly commitment or redistributing time.
          </Alert>
        )}
      </TreeBackground>



      {/* Age and Income Input Dialog */}
      <Dialog open={agePromptOpen} onClose={() => setAgePromptOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Personal Information for Health Impact</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            To calculate your personalized healthworth, we need some basic information:
          </Typography>
          
          <TextField
            label="Your Current Age"
            type="number"
            value={userAge || ''}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                setUserAge(null);
              } else {
                const age = parseInt(value);
                if (!isNaN(age)) {
                  setUserAge(age);
                }
              }
            }}
            inputProps={{ min: 18, max: 100 }}
            fullWidth
            sx={{ mb: 2 }}
            helperText="Used to calculate years until retirement (67)"
            error={userAge !== null && (userAge < 18 || userAge > 100)}
          />
          
          <TextField
            label="Current Annual Income"
            type="number"
            value={currentIncome}
            onChange={(e) => {
              const income = parseFloat(e.target.value);
              if (income > 0) {
                setCurrentIncome(income);
              }
            }}
            inputProps={{ min: 0, step: 1000 }}
            fullWidth
            sx={{ mb: 2 }}
            helperText="Projected with 2% annual growth to retirement"
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
            }}
          />
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Your healthworth is calculated as: Future retirement income × Years of life gained
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAgePromptOpen(false)}>
            Skip
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              if (userAge && userAge >= 18 && userAge <= 100) {
                localStorage.setItem('userAge', userAge.toString());
                localStorage.setItem('currentIncome', currentIncome.toString());
                setAgePromptOpen(false);
              }
            }}
            disabled={!userAge || userAge < 18 || userAge > 100}
          >
            Save & Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Talent Detail Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedTalent?.icon}
            <Box>
              <Typography variant="h6">
                {selectedTalent?.name}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Rank {selectedTalent?.currentRank} / {selectedTalent?.maxRank}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {selectedTalent?.description}
          </Typography>
          
          {!isPremium && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Premium Required:</strong> Free users can explore all talent trees but cannot invest time or track progress. 
                Upgrade to Premium to unlock the full health optimization experience!
              </Typography>
            </Alert>
          )}
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Time Commitment: {selectedTalent?.timeRequired} minutes per week per rank
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Total time if fully upgraded: {selectedTalent ? selectedTalent.timeRequired * selectedTalent.maxRank : 0} minutes per week
            </Typography>
            
            {/* Progress Indicator */}
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Progress
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedTalent?.currentRank} / {selectedTalent?.maxRank} ranks
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={selectedTalent ? (selectedTalent.currentRank / selectedTalent.maxRank) * 100 : 0}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: currentTree.color,
                  },
                }}
              />
            </Box>
            
            {selectedTalent && selectedTalent.currentRank > 0 && (
              <Typography variant="body2" color="text.secondary">
                Current weekly commitment: {selectedTalent.currentRank * selectedTalent.timeRequired} minutes
              </Typography>
            )}
          </Box>

          {selectedTalent?.requires && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Prerequisites:
              </Typography>
              {selectedTalent.requires.map(reqId => {
                const reqTalent = currentTree.talents.find(t => t.id === reqId);
                return (
                  <Chip
                    key={reqId}
                    label={reqTalent?.name || reqId}
                    size="small"
                    color={reqTalent?.currentRank ? 'success' : 'default'}
                    sx={{ mr: 1 }}
                  />
                );
              })}
            </Box>
          )}

          {selectedTalent && selectedTalent.currentRank < selectedTalent.maxRank && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Next Rank Benefits:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enhanced effectiveness and new capabilities will be unlocked.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: 3 }}>
          <Button onClick={() => setDialogOpen(false)} variant="outlined">
            Close
          </Button>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {selectedTalent && selectedTalent.currentRank > 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Current: Rank {selectedTalent.currentRank}/{selectedTalent.maxRank}
              </Typography>
            )}
            
            {selectedTalent && canUpgradeTalent(selectedTalent, currentTree) && isPremium && (
              <Button
                variant="contained"
                onClick={handleUpgradeTalent}
                sx={{ backgroundColor: currentTree.color }}
              >
                {selectedTalent.currentRank === 0 ? 'Start' : 'Upgrade'} 
                {' '}→ Rank {selectedTalent.currentRank + 1}
                {' '}({selectedTalent.timeRequired} min/week)
              </Button>
            )}
            
            {selectedTalent && canUpgradeTalent(selectedTalent, currentTree) && !isPremium && (
              <Button
                variant="contained"
                disabled
                sx={{ 
                  backgroundColor: 'rgba(128, 128, 128, 0.3)',
                  color: 'rgba(255, 255, 255, 0.5)',
                  cursor: 'not-allowed'
                }}
              >
                Premium Required
              </Button>
            )}
            
            {selectedTalent && !canUpgradeTalent(selectedTalent, currentTree) && selectedTalent.unlocked && (
              <Button disabled variant="contained">
                {selectedTalent.currentRank >= selectedTalent.maxRank 
                  ? `Maxed Out (Rank ${selectedTalent.maxRank})` 
                  : 'Insufficient Time'
                }
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Rank Selection Dialog */}
      <Dialog open={rankSelectionOpen} onClose={() => setRankSelectionOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedTalentForRanks?.talent.icon}
            <Box>
              <Typography variant="h6">
                Select Ranks for {selectedTalentForRanks?.talent.name}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Choose how many ranks to commit to this week
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {selectedTalentForRanks?.talent.description}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Each rank requires {selectedTalentForRanks?.talent.timeRequired} minutes per week.
              Maximum ranks available: {selectedTalentForRanks?.talent.maxRank}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Select Number of Ranks:
            </Typography>
            
            <Box sx={{ px: 2 }}>
              <Slider
                value={selectedRanks}
                onChange={(e, value) => setSelectedRanks(value as number)}
                min={1}
                max={selectedTalentForRanks?.talent.maxRank || 1}
                step={1}
                marks
                valueLabelDisplay="on"
                valueLabelFormat={(value) => `Rank ${value}`}
                sx={{
                  color: selectedTalentForRanks?.tree.color || '#2196f3',
                  '& .MuiSlider-thumb': {
                    width: 24,
                    height: 24,
                  },
                  '& .MuiSlider-track': {
                    height: 8,
                  },
                  '& .MuiSlider-rail': {
                    height: 8,
                  }
                }}
              />
            </Box>
          </Box>

          {/* Time Commitment Summary */}
          <Box sx={{ backgroundColor: 'rgba(0, 0, 0, 0.05)', p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Weekly Commitment Summary:
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Ranks Selected:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {selectedRanks} / {selectedTalentForRanks?.talent.maxRank}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Time per Rank:</Typography>
              <Typography variant="body2">
                {selectedTalentForRanks?.talent.timeRequired} minutes/week
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
              <Typography variant="body2">Total Weekly Time:</Typography>
              <Typography variant="body2" sx={{ color: selectedTalentForRanks?.tree.color || '#2196f3' }}>
                {(selectedTalentForRanks?.talent.timeRequired || 0) * selectedRanks} minutes/week
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              ≈ {Math.round(((selectedTalentForRanks?.talent.timeRequired || 0) * selectedRanks) / 7)} minutes per day
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setRankSelectionOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleRankSelection(selectedRanks)}
            sx={{
              backgroundColor: selectedTalentForRanks?.tree.color || '#2196f3',
              '&:hover': {
                backgroundColor: selectedTalentForRanks?.tree.color || '#1976d2',
                filter: 'brightness(0.9)'
              }
            }}
          >
            Select {selectedRanks} Rank{selectedRanks > 1 ? 's' : ''} 
            ({(selectedTalentForRanks?.talent.timeRequired || 0) * selectedRanks} min/week)
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TalentTree; 